import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Party } from "@/lib/types";
import { listParties } from "./parties.api";

export interface ParentChoice {
  id: string;
  name: string;
  code: string;
}

/**
 * The group (concern) a relation belongs to: one relation, found by name or number. What TALIS knew and AFAS does not,
 * so WLX keeps it (Camiel, 21-10-2026).
 */
export function ParentField({
  partyId,
  value,
  onChange,
  disabled,
}: {
  partyId: string;
  value: ParentChoice | null;
  onChange: (value: ParentChoice | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  // What the last search found; null while there is nothing to search for.
  const [items, setItems] = useState<Party[] | null>(null);
  const latest = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setItems(null);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      listParties({ q })
        .then((page) => {
          if (request === latest.current) setItems(page.data);
        })
        .catch(() => {
          if (request === latest.current) setItems([]);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  const choices = (items ?? []).filter((item) => item.id !== partyId).slice(0, 8);

  return (
    <div className="wl-field">
      <label className="wl-field__label" htmlFor="party-parent">
        {t("parties.parentLabel")}
      </label>
      {value ? (
        <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center" }}>
          <span className="t-body-sm">
            {value.name} <span className="t-mono t-caption t-muted">{value.code}</span>
          </span>
          <span className="wl-spacer" />
          {!disabled && (
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(null)}>
              {t("parties.dossiersWithRemove")}
            </button>
          )}
        </div>
      ) : (
        disabled && <span className="t-body-sm t-muted">{t("parties.parentNone")}</span>
      )}
      {!disabled && !value && (
        <>
          <input
            id="party-parent"
            className="wl-input"
            type="search"
            value={text}
            autoComplete="off"
            placeholder={t("orders.customerSearchPlaceholder")}
            onChange={(e) => setText(e.target.value)}
          />
          {items !== null && choices.length === 0 && <span className="t-caption t-muted">{t("orders.customerNothingFound")}</span>}
          {choices.length > 0 && (
            <ul className="wl-vstack" style={{ listStyle: "none", margin: 0, padding: 0, gap: "var(--space-0_5)", alignItems: "stretch" }}>
              {choices.map((party) => (
                <li key={party.id}>
                  <button
                    type="button"
                    className="wl-btn wl-btn--ghost wl-btn--sm"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left", height: "auto", padding: "var(--space-1_5) var(--space-2)" }}
                    onClick={() => {
                      onChange({ id: party.id, name: party.name, code: party.code });
                      setText("");
                      setItems(null);
                    }}
                  >
                    <span className="t-body-sm">
                      {party.name} <span className="t-mono t-caption t-muted">{party.code}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <span className="wl-field__help">{t("parties.parentHelp")}</span>
    </div>
  );
}
